/**
 * battleDemoTypes.ts — 戦闘デモプレイの画面が扱う型と API の呼び出し
 *
 * 応答の形は SYS/Baunheim/battle-demo/scripts/demo.js が返す json に合わせる。
 */

export const WORLD = "Baunheim";

export interface Cost { resource: string; amount: number }

export interface Pool { type: string; current: number; max: number }

/** 人物の詳細。選択画面・戦闘・購入と修理のどこからでも同じ形で開く */
export interface DetailView {
  id: string; name: string; sub: string;
  hp: { current: number; max: number }; pools: Pool[]; shield: number; money: number;
  base: Record<string, number>; judgment: Record<string, number>; resistance: Record<string, number>; damage_taken: Record<string, number>;
  modifiers: { label: string; kind: string; stacks: number; turns_left: number | string | null; summary: string }[];
  parts: { name: string; blocked: boolean; items: { name: string; summary: string; durability: { max: number; current: number } | null; pool: Pool | null }[] }[];
  skills: {
    name: string; kind: string; description: string; aspects: string[]; cost: Cost[];
    cooldown?: number; uses?: number | null; uses_left: number | null; cooldown_left: number; granted?: boolean;
  }[];
  items: { name: string; count: number; description: string }[];
  labels: { base: Record<string, string>; judgment: Record<string, string>; resistance: Record<string, string> };
}

export interface ItemView {
  id: string; slug: string; name: string; count: number; price: number; sell: number; bulk: number;
  equip_type: string | null; equip_label: string | null; equipped: boolean; locked: boolean;
  durability: { max: number; current: number } | null; repair: number; summary: string;
}

export interface CharView {
  key?: string; source?: string; detail?: DetailView;
  id: string; name: string; title: string; blurb: string; race: string; gender: string; money: number;
  hp: { current: number; max: number }; pools: Pool[];
  base: Record<string, number>; judgment: Record<string, number>; load: { current: number; max: number };
  growth: Record<string, number>;
  equipment: { name: string; kind: string; layers: { layer: string; name: string; capacity: number; accepts: string[]; items: string[] }[] }[];
  items: ItemView[];
  skills: { key: string; name: string; type: string; description: string; cost: Cost[] }[];
}

export interface UnitView {
  id: string; name: string; side: "ally" | "enemy"; slot: number; source: string | null;
  /** 乗っている乗せる者の id・席の名・乗っている者の id・乗せる定義を持つか・誰も乗っていない乗り物か */
  carrier?: string | null; seat?: string | null; riders?: string[]; carry?: boolean; parked?: boolean;
  hp: { current: number; max: number }; pools: Pool[]; alive: boolean; guarding: boolean; shield: number;
  modifiers: { name: string; label: string; short?: string | null; kind: string; stacks: number; turns_left: number | string | null; summary: string }[];
  intent?: { kind: string; name: string; targets: string[] } | null;
}

export interface ActionOption {
  kind: "attack" | "skill" | "item" | "board" | "alight"; key: string; name: string; description: string; selectable: boolean; reason: string | null;
  cost: Cost[]; uses_left: number | null; cooldown: number; cooldown_left: number; count: number | null; aspects: string[];
  weapon: string | null; item: string | null;
  /** 乗せる者の行動なら carrier。user は行う者（乗せる者）の名 */
  via?: string | null; user?: string | null;
  target: { side: string; scope: string; state: string; count: number | null; pick: boolean; candidates: string[] };
}

export interface UnitOptions { id: string; actions: ActionOption[]; fourth: string | null; flee_blocked: boolean }

export interface UnitSnap { hp: number; max: number; pools: number[]; shield: number; in: boolean; guarding: boolean; carrier?: string | null }

/** 戦闘ログの 1 行と、その行の時点の HP の写し */
export interface LogEvent { text: string; snap: Record<string, UnitSnap> | null }

export interface BattleView { id: string; turn: number; result: string | null; allies: UnitView[]; enemies: UnitView[]; awaiting: string[] }

export interface BattleData {
  stage: number; boss: boolean;
  view: BattleView;
  options: Record<string, UnitOptions>;
  details: Record<string, DetailView>;
  log: string[]; log_from: number; events: LogEvent[];
}

export interface Plan { kind: string; skill?: string; weapon?: string; item?: string; targets: string[]; via?: string }

export interface RunSummary {
  id: string; stage: number; phase: "battle" | "shop"; wins: number;
  party: { id: string; name: string; title: string }[];
  last: { result: string; stage: number; reward?: { total: number; share: number }; growth?: Record<string, Record<string, number>>; shop?: boolean } | null;
}

export interface StockItem {
  slug: string; name: string; price: number; bulk: number; group: string; equip_type: string | null; equip_label: string | null; stackable: boolean; summary: string; durability: number | null;
}

export interface HistoryRun {
  id: string; started: string; ended: string; result: string; stage: number; wins: number;
  party: { name: string; title: string }[];
  battles: { stage: number; id: string; result: string; turns: number; boss: boolean; enemies: string[] }[];
}

export interface DemoResponse {
  error?: string;
  run?: RunSummary | null;
  characters?: CharView[];
  history?: HistoryRun[];
  battle?: BattleData;
  shop?: { visit: number; next_stage: number; stock: StockItem[]; party: CharView[]; labels: { base: Record<string, string>; judgment: Record<string, string> } };
  log?: string;
}

export async function callDemo(op: string, args?: unknown): Promise<DemoResponse> {
  const res = await fetch("/api/admin/battle-demo", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ world: WORLD, op, args: args ?? {} }),
  });
  return (await res.json()) as DemoResponse;
}

/** cost の ammo.{弾の種類} の表記名 */
const AMMO_LABEL: Record<string, string> = { arrow: "矢", bolt: "クロスボウの矢", bullet: "弾丸", stone: "投げ石" };

export function costLabel(c: Cost): string {
  const name =
    c.resource === "hp"
      ? "HP"
      : c.resource.startsWith("money.")
        ? c.resource.slice(6).toUpperCase()
        : c.resource.startsWith("ammo.")
          ? AMMO_LABEL[c.resource.slice(5)] ?? c.resource
          : c.resource.toUpperCase();
  return `${name} ${c.amount < 0 ? -c.amount : `+${c.amount}`}`;
}

export const BASE_LABEL: Record<string, string> = {
  str: "筋力", dex: "技巧", agi: "敏捷", def: "防御", int: "知能", mnd: "精神", fth: "信仰", cha: "魅力", per: "知覚", lck: "幸運",
};
